import React from "react";
import { Navigate } from "react-router-dom";

import { ChatHeader } from "./components/ChatHeader";
import { ChatInput } from "./components/ChatInput";
import { ChatMessages } from "./components/ChatMessages";

import { useRbacPrimaryRolePermissions } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import { useReopenArtifact } from "./useReopenArtifact";
import { useReportingChatStream } from "./useReportingChatStream";

export const ReportAnalyticsChat: React.FC = () => {
	const { value: isEnabled, query } = useFeatureFlagQuery("ff-is-ai-chat-reports-enabled", false);
	const { data: permissions, isLoading: isPermissionsLoading } = useRbacPrimaryRolePermissions("report_analytics");
	// The endpoint's actual permission — broader adminPermissions would 403 on every request here.
	const canCreateArtifact = !!permissions?.includes("report_analytics:createArtifact");

	// Don't fire the artifact GET/render before the gate allows it — avoids an error toast pre-redirect.
	const gateReady = !query.isLoading && !isPermissionsLoading;
	const gateAllowed = gateReady && isEnabled && canCreateArtifact;

	// One stream instance for the page — ChatInput and the suggestion chips both send through it,
	// so they share a single abort controller (two instances would not cancel each other). Declared
	// before useReopenArtifact so its abort can be handed in and cancel a live stream whose frames
	// (notably reporting_chat_notes) would otherwise keep landing after the artifact identity changes.
	const { sendQuery, isStreaming, abort } = useReportingChatStream();
	const { artifactId, isLoadingArtifact, isArtifactError, isRenderingArtifact, renderError } = useReopenArtifact({
		enabled: gateAllowed,
		abortStream: abort,
	});

	if (query.isLoading || isPermissionsLoading) {
		return <div className="flex h-full items-center justify-center text-sm text-neutral-400">Loading…</div>;
	}

	if (!isEnabled || !canCreateArtifact) {
		return <Navigate to="/analytics" replace />;
	}

	const isReopening = !!artifactId && (isLoadingArtifact || isRenderingArtifact);
	// Narrower than isReopening (which also covers the render, up to RENDER_TIMEOUT_MS, and only
	// drives the banner below): the store-clobbering reset() fires as soon as the GET resolves, so
	// sending must be blocked only for the GET itself — gating the render too would resurrect the
	// sluggish reopen this flow was built to avoid.
	const isArtifactLoading = !!artifactId && isLoadingArtifact;

	return (
		<div className="flex h-full min-h-0 flex-col bg-white">
			<ChatHeader />
			{isReopening && (
				<div className="mx-auto w-full max-w-3xl px-4 pt-2">
					<div className="rounded border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
						Refreshing saved report…
					</div>
				</div>
			)}
			<ChatMessages sendQuery={sendQuery} isStreaming={isStreaming} isDisabled={isArtifactLoading} />
			{artifactId && (isArtifactError || renderError) && (
				<div className="mx-auto w-full max-w-3xl px-4 pt-2">
					<div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
						{renderError === "timeout"
							? "The saved report took too long to refresh. You can still ask a new question below."
							: "Could not load the saved report. You can still ask a new question below."}
					</div>
				</div>
			)}
			<ChatInput sendQuery={sendQuery} isStreaming={isStreaming} isDisabled={isArtifactLoading} />
		</div>
	);
};

export default ReportAnalyticsChat;
